import { useEffect, useState } from 'react';
import Navigation from './components/Navigation';
import HeroSection from './components/HeroSection';
import FeaturesSection from './components/FeaturesSection';
import JobsSection from './components/JobsSection';
import AboutSection from './components/AboutSection';
import TestimonialSection from './components/TestimonialSection';
import CTASection from './components/CTASection';
import Footer from './components/Footer';
import KakaoFloatingButton from '../../components/feature/KakaoFloatingButton';
import { useSEO } from '../../utils/seo';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useSEO({
    title: '온니잡 - 퀸알바의 선택',
    description: '유흥·바·모던바·호스트바 등 나이트라이프 업종 전문 구인구직 플랫폼. 개인회원 무료, 안전한 채팅 시스템, 검증된 인재 정보를 온니잡에서 만나보세요.',
    keywords: '유흥업 채용,퀸알바,여우알바,호스트바,호빠,웨이터나라,호빠나라,호짱,여우알바,러브알바,나나알바,미드나잇테라스,강남키티,룸빵닷컴,버블알바,강남알바,하퍼,텐프로,텐카페,쩜오,구구단,퍼펙트,유앤미,사라있네',
    canonical: `${siteUrl}/`,
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebSite',
          '@id': `${siteUrl}/#website`,
          url: siteUrl,
          name: '온니잡',
          description: '유흥·바·모던바·호스트바 등 나이트라이프 업종 전문 구인구직 플랫폼',
          inLanguage: 'ko-KR',
          potentialAction: {
            '@type': 'SearchAction',
            target: {
              '@type': 'EntryPoint',
              urlTemplate: `${siteUrl}/jobs?search={search_term_string}`
            },
            'query-input': 'required name=search_term_string'
          }
        },
        {
          '@type': 'Organization',
          '@id': `${siteUrl}/#organization`,
          name: '온니잡',
          alternateName: '온니잡',
          url: siteUrl,
          logo: {
            '@type': 'ImageObject',
            url: `${siteUrl}/logo.png`,
            width: 200,
            height: 60
          },
          description: '나이트라이프 업계 전문 구인구직 플랫폼',
          foundingDate: '2024',
          areaServed: {
            '@type': 'Country',
            name: '대한민국'
          },
          contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'customer service',
            availableLanguage: 'Korean'
          }
        },
        {
          '@type': 'WebPage',
          '@id': `${siteUrl}/#webpage`,
          url: siteUrl,
          name: '온니잡 - 퀸알바의 선택',
          isPartOf: { '@id': `${siteUrl}/#website` },
          about: { '@id': `${siteUrl}/#organization` },
          description: '유흥·바·모던바·호스트바 등 나이트라이프 업종 전문 구인구직 플랫폼. 개인회원 무료, 안전한 채팅 시스템, 검증된 인재 정보를 온니잡에서 만나보세요.',
          inLanguage: 'ko-KR',
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '홈',
                item: siteUrl
              }
            ]
          }
        },
        {
          '@type': 'FAQPage',
          '@id': `${siteUrl}/#faq`,
          mainEntity: [
            {
              '@type': 'Question',
              name: '온니잡은 어떤 서비스인가요?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '온니잡은 유흥업, 바텐더, 모던바, 호스트바 등 나이트라이프 업계 전문 구인구직 플랫폼입니다. 개인회원은 무료로 가입하여 채용공고를 확인하고 지원할 수 있습니다.'
              }
            },
            {
              '@type': 'Question',
              name: '회원가입 비용이 있나요?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '개인회원 가입은 완전 무료입니다. 회원가입 후 모든 채용공고를 무제한으로 열람하고 지원할 수 있습니다.'
              }
            },
            {
              '@type': 'Question',
              name: '어떤 직종의 채용공고가 있나요?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '바텐더, 홀 매니저, 서버, DJ, 보안 요원 등 나이트라이프 업계 전반의 다양한 직종 채용공고를 제공합니다. 서울, 부산, 대구 등 전국 주요 도시의 공고를 확인할 수 있습니다.'
              }
            },
            {
              '@type': 'Question',
              name: '채용공고는 얼마나 자주 업데이트되나요?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '온니잡의 채용공고는 매일 실시간으로 업데이트됩니다. 최신 공고를 놓치지 않도록 정기적으로 방문해 주세요.'
              }
            },
            {
              '@type': 'Question',
              name: '지원 후 결과는 어떻게 확인하나요?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '마이페이지의 지원 내역에서 지원한 공고의 현황을 실시간으로 확인할 수 있습니다. 업체에서 연락이 오면 알림을 통해 안내해 드립니다.'
              }
            }
          ]
        }
      ]
    }
  });

  return (
    <div className="min-h-screen bg-dark-deep">
      <Navigation scrolled={scrolled} />
      <HeroSection />
      <FeaturesSection />
      <JobsSection />
      <AboutSection />
      <TestimonialSection />
      <CTASection />
      <Footer />
      <KakaoFloatingButton />
    </div>
  );
}
